import * as firebase from 'firebase/app';
import 'firebase/auth';

import { IAuthGateway } from '@/core/interface/auth/gateway';
import { User } from '@/core/entity/user';

export class FirebaseAuthGateway implements IAuthGateway {
  public static getIdToken() {
    const user = firebase.auth().currentUser;
    return user === null ? Promise.resolve(null) : user.getIdToken();
  }

  constructor(
    private readonly auth: firebase.auth.Auth,
  ) { }

  public async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    const user = firebase.auth().currentUser!;

    const credential = firebase.auth.EmailAuthProvider.credential(user.email!, oldPassword);

    await user!.reauthenticateWithCredential(credential);

    await user.updatePassword(newPassword);
  }

  public async getCurrentUser(waitForInit?: boolean): Promise<User | null> {
    if (waitForInit) {
      await this.waitForAuthStateChange();
    }

    const user = await this.auth.currentUser;

    if (user === null) {
      return null;
    }

    const token = await user.getIdTokenResult();

    return new User(
      user.uid,
      user.displayName!,
      user.email!,
      '',
      token.claims.roles
    );
  }

  public async reset(email: string): Promise<undefined> {
    await this.auth.sendPasswordResetEmail(email);

    return;
  }

  public async signIn(email: string, password: string): Promise<User | null> {
    const res = await this.auth.signInWithEmailAndPassword(email, password);

    const user = res.user;
    if (user === null) {
      return null;
    }

    const token = await user.getIdTokenResult();

    return new User(
      user.uid,
      user.displayName!,
      user.email!,
      '',
      token.claims.roles
    );
  }

  public async signOut(): Promise<void> {
    await this.auth.signOut();
  }

  private waitForAuthStateChange() {
    return new Promise<void>((resolve, reject) => {
      const unsubscribe = this.auth.onIdTokenChanged((user) => {
        resolve();
        unsubscribe();
      });
    });
  }
}
